import {
	PropsWithChildren,
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { AppType } from "client/Api/Apps/common";
import { AppTypeSpecificTileSettings } from "../../RenderState/common";
import { InteractionState, TileContext } from "../TileContext";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { LiveTileContent } from "../LiveTileContent";
import {
	IntegrationInterestedParty,
	IntegrationLiveInformationProvider,
	IntegrationTileItemModel,
	IntegrationUpdateModel
} from "./provider";
import { AppIntegrationLiveInformationResultStatus, ColoredValueModel } from "client/Api/Workspace/LiveInformation/Integration/integrationLiveInformation";
import { IntegrationFlowMultipleAuthenticationsAreRequiredModel } from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import { never } from "client_legacy/ndaw/errors";
import {
	BusyIndicator,
	DateString,
	ListItem
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	AuthenticateIntegrationFlowResultKind,
	useTryAuthenticateIntegrationFlow
} from "client/Features/Layout/TitleBar/GlobalSearch/useAuthenticateIntegrationFlow";
import {
	generateTrackingId,
	isNotNil,
	isNotNilOrWhiteSpace,
	setTrackingId
} from "client/Misc/utils";
import styles from "./IntegrationLiveTileContent.module.less";
import { TimeZone } from "client/DateAndTime/timezones";
import { ResultMapperColor } from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type IntegrationTileSettings = AppTypeSpecificTileSettings<AppType.Integration>;

export type IntegrationLiveTileContentProps = {
	settings: IntegrationTileSettings;
	provider: IntegrationLiveInformationProvider;
	onNavigation: () => void;
	userTimezone: TimeZone;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const IntegrationLiveTileContent = (props: IntegrationLiveTileContentProps): ReactElement => {
	const {
		settings,
		provider,
		onNavigation,
		userTimezone,
		onOpenTileMenu
	} = props;

	const {
		id: tileId,
		icon,
		name,
		environmentAppId,
		conditionalAccess,
		maintenanceWindow,
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		conditionalAccess,
		maintenanceWindow,
		navigationHandler: onNavigation,
		appIsLicensed,
		appIsNotAccessible
	});

	const isUnavailable: boolean = isNotNil(maintenanceWindow)
		|| isNotNil(conditionalAccess)
		|| interactionState === InteractionState.Disabled;

	if (isNotNil(maintenanceWindow) || isNotNil(conditionalAccess)) {
		return (
			<LiveTileContent
				icon={icon}
				name={name}
				onOpenTileMenu={onOpenTileMenu}
				overlayStatusIcon={<TileStatus />}
				overlay={overlayState}
			/>
		);
	}

	return (
		<IntegrationLiveTileContentInternal
			settings={settings}
			provider={provider}
			onNavigation={onNavigation}
			userTimezone={userTimezone}
			tileId={tileId}
			icon={icon}
			name={name}
			environmentAppId={environmentAppId}
			isUnavailable={isUnavailable}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
		/>
	);
};
IntegrationLiveTileContent.displayName = "IntegrationLiveTileContent";

type IntegrationLiveTileContentInternalProps = {
	settings: IntegrationTileSettings;
	provider: IntegrationLiveInformationProvider;
	onNavigation: () => void;
	userTimezone: TimeZone;
	tileId: number;
	icon: AppImageDisplayInfo;
	name: string;
	environmentAppId: number;
	isUnavailable: boolean;
	overlay: LiveTileOverlayState;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

const IntegrationLiveTileContentInternal = (props: IntegrationLiveTileContentInternalProps): ReactElement => {
	const {
		settings,
		provider,
		onNavigation,
		userTimezone,
		tileId,
		icon,
		name,
		environmentAppId,
		isUnavailable,
		overlay,
		onOpenTileMenu
	} = props;

	const [state, setState] = useState<State>({
		status: "isLoading"
	});

	const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);

	const [authenticationDialogs, authenticate] = useTryAuthenticateIntegrationFlow();

	const {
		connectionId,
		hideHeader,
		hideFooter,
		navigationUrl
	} = settings;

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			onNavigation();
		},
		[addAnalyticsEvent, onNavigation]
	);

	const retryHandler = useCallback(
		(): void => {
			provider.forceReload();
		},
		[provider]
	);

	const party = useMemo(
		(): IntegrationInterestedParty => ({
			partyId: tileId,
			resourceId: connectionId,
			informAboutGlobalRetrievalFailure: () => {
				setState({
					status: "globalFailure"
				});
			},
			informAboutConsecutiveErrors: () => {
				setState({
					status: "globalFailure"
				});
			},
			update: (updateModel: IntegrationUpdateModel) => {
				switch (updateModel.status) {
					case AppIntegrationLiveInformationResultStatus.Failure:
						setState({
							status: "globalFailure"
						});
						break;
					case AppIntegrationLiveInformationResultStatus.AuthenticationIsRequired:
						setState({
							status: "authenticationRequired",
							authenticationModel: updateModel.model
						});
						break;
					case AppIntegrationLiveInformationResultStatus.Success:
						setState({
							status: "loaded",
							items: updateModel.items
						});
						break;
					default:
						never(updateModel);
				}
			}
		}),
		[tileId, connectionId]
	);

	useEffect(
		(): (() => void) | undefined => {
			if (isUnavailable) {
				return undefined;
			}

			provider.register(party, { items: true });

			return (): void => {
				provider.unregister(party);
			};
		},
		[
			provider,
			party,
			isUnavailable
		]
	);

	const authenticateConfiguration = useMemo(
		(): { onRetry: () => void; retryText: string; } | undefined => {
			if (state.status !== "authenticationRequired") {
				return undefined;
			}

			const {
				authenticationModel,
				wasInvoked
			} = state;

			if (wasInvoked) {
				return {
					onRetry: (): void => {
						retryHandler();
					},
					retryText: localize("_Finish")
				};
			}

			return {
				onRetry: async (): Promise<void> => {
					if (isAuthenticating) {
						return;
					}

					const {
						authentications: missingAuthentications
					} = authenticationModel;

					const result: TryResult<AuthenticateIntegrationFlowResultKind> = await authenticate(
						name,
						missingAuthentications,
						setIsAuthenticating
					);

					if (!result.wasSuccessful) {
						return;
					}

					switch (result.result) {
						case "completed":
							retryHandler();
							break;
						case "wasInvoked":
							setState(x => {
								if (x.status !== "authenticationRequired") {
									return x;
								}

								return {
									...x,
									wasInvoked: true
								};
							});
							break;
						default:
							never(result.result);
					}
				},
				retryText: localize("_Authenticate")
			};
		},
		[
			state,
			name,
			isAuthenticating,
			retryHandler,
			authenticate
		]
	);

	const renderContent = (): ReactElement => {
		switch (state.status) {
			case "globalFailure":
				return (
					<LiveTileContent.NoResults
						icon="gear"
						title={localize("_UnableToRetrieveItems")}
						message={localize("_WeWereUnableToRetrieveTheItems")}
						onRetry={retryHandler}
						trackingId="global-failure"
					/>
				);
			case "isLoading":
				return (<BusyIndicator />);
			case "authenticationRequired":
				return (
					<>
						<LiveTileContent.NoResults
							{...authenticateConfiguration}
							trackingId="authentication-required"
							icon="gear"
							title={localize("_AuthenticationRequired")}
							message={{
							template: localize("_ToUseNameAuthenticationIsNeeded"),
							replacements: [
									{ what: "{{Name}}", filler: name }
								]
							}}
						/>
						{isAuthenticating && <BusyIndicator />}
						{authenticationDialogs}
					</>
				);
			case "loaded":
				if (state.items.length === 0) {
					return (
						<LiveTileContent.NoResults
							icon="list"
							title={localize("_NoItemsToShow")}
							message={localize("_ThereAreCurrentlyNoItemsToShowHere")}
							trackingId="no-items"
						/>
					);
				}

				return (
					<LiveTileContent.Scrollable>
						<List
							items={state.items}
							userTimezone={userTimezone}
						/>
					</LiveTileContent.Scrollable>
				);
			default:
				return never(state);
		}
	};

	return (
		<LiveTileContent
			name={name}
			icon={icon}
			overlay={overlay}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			{!hideHeader && (
				<LiveTileContent.Header icon={icon}>
					<LiveTileContent.Title text={name} />
				</LiveTileContent.Header>
			)}

			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					<LiveTileContent.BusyIndicatorContainer>
						{renderContent()}
					</LiveTileContent.BusyIndicatorContainer>
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			{navigationUrl && !hideFooter && (
				<LiveTileContent.Footer
					name={name}
					onClick={navigateHandler}
				/>
			)}
		</LiveTileContent>
	);
};
IntegrationLiveTileContentInternal.displayName = "IntegrationLiveTileContentInternal";

type State = {
	status: "globalFailure" | "isLoading";
} | {
	status: "authenticationRequired";
	authenticationModel: IntegrationFlowMultipleAuthenticationsAreRequiredModel;
	wasInvoked?: boolean;
} | {
	status: "loaded";
	items: readonly IntegrationTileItemModel[];
};

type ListProps = {
	items: readonly IntegrationTileItemModel[];
	userTimezone: TimeZone;
};

const List = (props: ListProps): ReactElement => {
	const {
		items,
		userTimezone
	} = props;

	return (
		<LiveTileContent.List>
			{items.map(item => (
				<li key={item.uniqueId}>
					<Item
						item={item}
						userTimezone={userTimezone}
					/>
				</li>
			))}
		</LiveTileContent.List>
	);
};
List.displayName = "List";

type ItemProps = {
	item: IntegrationTileItemModel;
	userTimezone: TimeZone;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		item,
		userTimezone
	} = props;

	const {
		title,
		dateTime,
		subtitle1,
		subtitle2,
		subtitle3,
		description
	} = item;

	const titleValue: string | undefined = title?.value;

	const computedTitle: string = isNotNilOrWhiteSpace(titleValue)
		? titleValue
		: localize("_NoTitle");

	return (
		<ListItem
			trackingId={generateTrackingId("integration-live-tile-item", `|${item.uniqueId}`)}
		>
			<ListItem.Content
				{...item.link
					? {
						href: item.link,
						isTargetBlank: true,
					}
					: {}
				}
				tooltip={titleValue}
				thumbnail={
					dateTime
						? (
							<Thumbnail
								dateTime={dateTime.value}
								color={dateTime.color}
								userTimezone={userTimezone}
							/>
						)
						: undefined
				}
			>
				<ListItem.Title
					tooltip={computedTitle}
				>
					<ColoredText
						trackingId="title-text"
						color={title?.color}
					>
						{computedTitle}
					</ColoredText>
				</ListItem.Title>
				<Subtitles
					subTitle1={subtitle1}
					subTitle2={subtitle2}
					subTitle3={subtitle3}
				/>
				{description?.value && (
					<div className={styles.description}>
						<ColoredText
							trackingId="description"
							color={description.color}
						>
							{description.value}
						</ColoredText>
					</div>
				)}
			</ListItem.Content>
		</ListItem>
	);
};
Item.displayName = "Item";

type ThumbnailProps = {
	dateTime: string;
	color: ResultMapperColor | null;
	userTimezone: TimeZone;
};

const Thumbnail = (props: ThumbnailProps): ReactElement => {
	const {
		dateTime,
		color,
		userTimezone
	} = props;

	return (
		<div
			className={styles.thumbnail}
		>
			<h4>
				<ColoredText
					trackingId="thumbnail-date"
					color={color}
				>
					<DateString
						value={dateTime}
						format="DM"
						timezone={userTimezone}
					/>
				</ColoredText>
			</h4>
			<div className={styles.thumbnailTime}>
				<ColoredText
					trackingId="thumbnail-time"
					color={color}
				>
					<DateString
						value={dateTime}
						format="T"
						timezone={userTimezone}
					/>
				</ColoredText>
			</div>
		</div>
	);
};
Thumbnail.displayName = "Thumbnail";

type SubtitlesProps = {
	subTitle1: ColoredValueModel<string> | null;
	subTitle2: ColoredValueModel<string> | null;
	subTitle3: ColoredValueModel<string> | null;
};

const Subtitles = (props: SubtitlesProps): ReactElement | null => {
	const {
		subTitle1,
		subTitle2,
		subTitle3
	} = props;

	if (
		!subTitle1?.value
			&& !subTitle2?.value
			&& !subTitle3?.value
	) {
		return null;
	}

	return (
		<ListItem.Row>
			<div className={styles.subtitlesContainer}>
				{(subTitle1?.value || subTitle2?.value) && (
					<div className={styles.subtitlesLeftSide}>
						{subTitle1?.value && (
							<ListItem.SubTitle>
								<ColoredText
									trackingId="sub-title-1"
									color={subTitle1.color}
								>
									{subTitle1.value}
								</ColoredText>
							</ListItem.SubTitle>
						)}

						{subTitle2?.value && (
							<>
								{subTitle1?.value && (
									<span>|</span>
								)}

								<ListItem.SubTitle>
									<ColoredText
										trackingId="sub-title-2"
										color={subTitle2.color}
									>
										{subTitle2.value}
									</ColoredText>
								</ListItem.SubTitle>
							</>
						)}
					</div>
				)}
				{subTitle3?.value && (
					<div className={styles.subtitlesRightSide}>
						<ListItem.SubTitle>
							<ColoredText
								trackingId="sub-title-3"
								color={subTitle3.color}
							>
								{subTitle3.value}
							</ColoredText>
						</ListItem.SubTitle>
					</div>
				)}
			</div>
		</ListItem.Row>
	);
};
Subtitles.displayName = "Subtitles";

type ColoredTextProps = {
	color: ResultMapperColor | nil;
	trackingId: string;
};

const ColoredText = (props: PropsWithChildren<ColoredTextProps>): ReactElement => {
	const {
		trackingId,
		color,
		children
	} = props;

	return (
		<span
			{...setTrackingId(trackingId)}
			className={
				isNotNil(color)
					? mappedColorToStyleMap[color]
					: undefined
			}
		>
			{children}
		</span>
	);
};
ColoredText.displayName = "ColoredText";

const mappedColorToStyleMap: Record<ResultMapperColor, string> = {
	[ResultMapperColor.Green]: styles.coloredText_success,
	[ResultMapperColor.Blue]: styles.coloredText_info,
	[ResultMapperColor.Orange]: styles.coloredText_warning,
	[ResultMapperColor.Red]: styles.coloredText_danger,
	[ResultMapperColor.Grey]: styles.coloredText_secondary,
	[ResultMapperColor.Default]: styles.coloredText_primary,
	[ResultMapperColor.Purple]: styles.coloredText_calendar6,
	[ResultMapperColor.Cyan]: styles.coloredText_calendar9
};
